import { ErrorResponse, useParams, useSearchParams } from 'react-router-dom';
import { PropsWithChildren, ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import collect from 'collect.js';
import classnames from 'classnames';
import moment from 'moment';
import {
  CheckCircle,
  ChevronLeft,
  Gift,
  HelpCircle,
  Mail,
  MapPin,
  Phone,
  ShoppingBag,
  Star,
  Truck,
  XCircle,
} from 'lucide-react';
import OrderManager from '../services/api/OrderManager';
import useAlert from '../hooks/utility/useAlert';
import {
  COUPON_TYPES,
  ORDER_FULFILLMENT_STATUSES,
  ORDER_METHODS,
  ORDER_PAYMENT_STATES,
  ORDER_PAYMENT_STATUSES,
  PUSHER_EVENTS,
  PaymentGateway,
  PaymentMethod,
  RoomServiceLocationType,
  SCREENS,
} from '../services/exports/Constants';
import useRouter from '../hooks/navigation/useRouter';
import DeliveryTracker from '../components-library/delivery/DeliveryTracker';
import useOrderFlow from '../hooks/order/useOrderFlow';
import PaymentDetails from '../components-library/payment/payment-methods/PaymentDetails';
import useDeepRef from '../hooks/utility/useDeepRef';
import AddressHelpers from '../services/helpers/AddressHelpers';
import CompanyDescription from '../components-library/company/CompanyDescription';
import LoadingPage from '../components-library/loading/LoadingPage';
import useFocus from '../hooks/utility/useFocus';
import { OFFSITE_RETURN_BUDGET_MS } from '../hooks/payment/useHandleSpreedlyRedirectResult';
import usePusher from '../hooks/global/usePusher';
import { orderTrackingChannel } from '../services/pusher/Channels';
import { CompanyDetailsInterface, Order } from '../services/exports/Interfaces';
import useUrlParams from '../hooks/utility/useUrlParams';
import useBasket from '../hooks/basket/useBasket';
import OrderPaymentBreakdown from '../components-library/order/OrderPaymentBreakdown';
import OrderRating from '../components-library/order/OrderRating';
import useAuth from '../hooks/global/useAuth';
import { CompleteBasketItem } from '../providers/BasketProvider';
import BasketItemModifiers from '../components-library/basket/BasketItemModifiers';
import ProfileManager from '../services/api/ProfileManager';
import useAppMethods from '../hooks/utility/useAppMethods';
import usePaymentFlow from '../hooks/payment/usePaymentFlow';
import PaymentManager from '../services/api/PaymentManager';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import useAnalytics from '../hooks/analytics/useAnalytics';
import toFlatArray from '../utils/to-flat.util';
import useMobileApp from '../hooks/mobile-app/useMobileApp';
import { Routes } from '../modules/mobile/exports/Enums';
import useOffersStuff from '../features/offers/hooks/useOffersStuff';
import useNotification from '../hooks/utility/useNotification';
import { useGetOrderQuery } from '../store/api-slices';
import { OrdersApiSliceTags, ordersApiSlice } from '../store/api-slices/OrdersApiSlice';
import { useDispatch, useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import IllustrationExplainer from '../components-library/IllustrationExplainer';
import ErrorIllustration from '../assets/illustrations/ErrorIllustration';
import WindowResource from '../services/resources/WindowResource';
import OrderTracker from '../components-library/order/OrderTracker';
import PickupTimeChangedNotice from '../components-library/order/PickupTimeChangedNotice';
import HelperMethods from '../services/exports/HelperMethods';
import nextPaymentStatusPollDelay from '../utils/paymentStatusPolling';
import { useRefreshSavedPaymentOptionsOnPaid } from '../hooks/payment/useRefreshSavedPaymentOptions';
import useOffsiteLateSuccess from '../hooks/payment/useOffsiteLateSuccess';
import pageDiagnostics, { DiagnosticAttrs } from '../services/helpers/PageDiagnostics';
import TestRestaurantNotice from '../components-library/company/TestRestaurantNotice';

/**
 * Waits before each automatic refetch of an order that did not load. iOS WebKit can fail every
 * request with no response for a moment when it restores a tab or opens an old order link
 * (SHOP-WEB-4); back-to-back retries all landed inside that window.
 */
export const ORDER_REFETCH_DELAYS_MS = [1_000, 2_000, 4_000];

export enum RedirectStatus {
  succeeded = 'succeeded',
  failed = 'failed',
}

export interface IOrderDetailsPage {
  uuid?: string;
  redirect_status?: RedirectStatus;
  /** `1` on the return from an offsite payment (iDEAL, Bancontact, …): see useOffsiteLateSuccess. */
  offsite?: string;
  onChangePaymentStatus?: (status: ORDER_PAYMENT_STATUSES) => void;
  closeModal?: () => void;
}

const Content = ({
  uuid,
  redirect_status,
  offsite,
  onChangePaymentStatus,
  closeModal,
}: IOrderDetailsPage): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { data: orderResponse } = useGetOrderQuery(uuid);
  const company = orderResponse?.data?.company;
  const alert = useAlert();
  const { notify } = useNotification();
  const { isLoggedIn } = useAuth();
  const { toggleInfoModal } = useAppMethods();
  const { isConnected, connect, disconnect } = usePusher();
  const { isMobileApp } = useMobileApp();
  const { navigate } = useRouter();
  const { clearBasket } = useBasket();
  const { addOngoingOrder, resetOrder } = useOrderFlow();
  const [, setSearchParams] = useSearchParams();
  const { trackCompletePurchase } = useAnalytics();
  const dispatch = useDispatch();
  const { hasCashbackProgram } = useCashbackProgram({ company });
  const { selectedPaymentMethod, findSavedPaymentMethod, getPaymentOptions, onlinePaymentsGateway } = usePaymentFlow();
  const paymentOptions = getPaymentOptions(company);
  const isStripeCheckout = onlinePaymentsGateway === PaymentGateway.Stripe;
  // Stripe: a card or PayPal account kept on this order is on the customer's next checkout without a
  // refresh. The other gateways keep their own rule (orderPaidHandler).
  const refreshSavedPaymentOptions = useRefreshSavedPaymentOptionsOnPaid();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [order, setOrder] = useState<Order>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [isAfterPurchase, setIsAfterPurchase] = useState(false);

  const orderRef = useDeepRef(order);
  const paymentStatusRef = useDeepRef(paymentStatus);
  const loadingOrderRef = useDeepRef(loadingOrder);
  const paymentFailureHandledRef = useRef(false);
  // On an offsite return the return handler (useHandleSpreedlyRedirectResult) owns a refused
  // payment for its budget: it sends the customer back to pay. Reporting the same failure here
  // too would stack a second modal on top of that.
  const returnHandlerOwnsFailureUntil = useRef(offsite === '1' ? Date.now() + OFFSITE_RETURN_BUDGET_MS : 0);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollStoppedRef = useRef(false);
  const pollFailureAlertedRef = useRef(false);
  const emptyOrderRetriesRef = useRef(0);
  const emptyOrderRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { reset } = useOffersStuff();
  const notePaymentStatus = useOffsiteLateSuccess({ offsite, redirect_status }, () => {
    setIsAfterPurchase(true);
    resetGlobalState();
  });

  const completeBasketItems = useMemo(() => {
    return (
      order &&
      order.line_items.map(
        (item) =>
          ({
            ...item,
            entry: item,
            modifiers: toFlatArray(item.modifiers, 'modifiers').map((entry) => ({ ...entry, entry })),
          }) as unknown as CompleteBasketItem,
      )
    );
  }, [order?.line_items]);

  const savedPaymentMethod = useMemo(
    () => findSavedPaymentMethod(selectedPaymentMethod),
    [selectedPaymentMethod, paymentOptions],
  );

  useEffect(() => {
    if (redirect_status === RedirectStatus.succeeded) {
      setIsAfterPurchase(true);
      resetGlobalState();
    }

    if (redirect_status === RedirectStatus.failed) {
      return orderFailedHandler();
    }
  }, [redirect_status]);

  useFocus(
    () => initData(),
    () => null,
    true,
  );

  useEffect(() => {
    onMount();

    return () => {
      pollStoppedRef.current = true;
      clearTimeout(pollTimerRef.current);
      clearTimeout(emptyOrderRetryTimerRef.current);
      disconnect();
    };
  }, []);

  useEffect(() => {
    loadOrderInfo();
  }, [paymentStatus]);

  useEffect(() => {
    onChangePaymentStatus?.(paymentStatus);
  }, [paymentStatus]);

  function resetGlobalState() {
    reset();
    resetOrder();
    clearBasket();
    dispatch(ordersApiSlice.util.invalidateTags([OrdersApiSliceTags.Order]));
  }

  async function onMount() {
    startPaymentStatusPolling();
    initData();
  }

  /**
   * Polls the derived payment state until it is terminal. Non-Spreedly gateways keep the original
   * 3/5/10 s schedule; a Spreedly 3DS settle can trail the customer's return by longer than that
   * (backend confirm retries, then the reconcile sweep), so it backs off within a bounded budget
   * instead of leaving "processing payment" on screen until Pusher happens to speak up.
   */
  function startPaymentStatusPolling() {
    const startedAt = Date.now();
    let attempt = 0;

    const poll = async () => {
      attempt += 1;

      const paymentState = await loadOrderPaymentStatus();

      if (pollStoppedRef.current || orderRef.current) {
        return;
      }

      const delayMs = nextPaymentStatusPollDelay({
        gateway: onlinePaymentsGateway,
        attempt,
        elapsedMs: Date.now() - startedAt,
        paymentState,
      });

      if (delayMs === null) {
        return;
      }

      pollTimerRef.current = setTimeout(poll, delayMs);
    };

    poll();
  }

  async function initData() {
    loadOrderInfo();

    if (!isConnected()) {
      return connectPusher();
    }
  }

  async function connectPusher() {
    const pusher = await connect();
    pusher
      .subscribe(orderTrackingChannel(uuid))
      .bind(PUSHER_EVENTS.ORDER_PAID, orderPaidHandler)
      .bind(PUSHER_EVENTS.ORDER_UPDATED, orderUpdatedHandler)
      .bind(PUSHER_EVENTS.ORDER_FAILED, orderFailedHandler);
  }

  function orderPaidHandler(data: Order) {
    if (isStripeCheckout) {
      refreshSavedPaymentOptions(data);
    } else if (
      isLoggedIn &&
      (selectedPaymentMethod === PaymentMethod.Card || selectedPaymentMethod === PaymentMethod.Paypal) &&
      !savedPaymentMethod
    ) {
      PaymentManager.getPaymentOptions(company?.id);
    }

    resetGlobalState();
    orderUpdatedHandler(data);
  }

  function orderUpdatedHandler(data: Order) {
    if (data.payment_status === ORDER_PAYMENT_STATUSES.PENDING) {
      return;
    }

    notePaymentStatus(data.payment_status);
    setPaymentStatus(data.payment_status);
    loadOrderInfo();
  }

  function orderFailedHandler() {
    toggleInfoModal({
      openModal: true,
      title: t('error_messages.payment_failed'),
      primaryButtonTitle: t('buttons.back_to_checkout'),
      onConfirm: () => {
        if (isMobileApp) {
          closeModal();
          navigate({ mobile: { path: Routes.Checkout } });

          return;
        }

        navigate({ web: { to: SCREENS.CHECKOUT } });
      },
    });
  }

  /** Returns the derived payment_state the backend reported (null when unknown), for the poll loop. */
  async function loadOrderPaymentStatus(): Promise<ORDER_PAYMENT_STATES | null> {
    if (orderRef.current) {
      return orderRef.current.payment_state ?? null;
    }

    const { success, response } = await OrderManager.getOrderPaymentStatus(uuid);

    if (!success) {
      // One alert per visit: the loop keeps polling through a transient failure, and a modal per
      // attempt would stack a dozen of them over a Spreedly budget.
      if (!pollFailureAlertedRef.current) {
        pollFailureAlertedRef.current = true;
        alert.error({ description: t('error_messages.failed_to_load') });
      }

      return null;
    }

    // An aborted request (e.g. the page navigating away mid-poll) resolves without a body.
    if (!response?.data?.data) {
      return null;
    }

    const { payment_state: paymentState, payment_status: status } = response.data.data;

    // payment_state is the derived discriminator: a failed/abandoned payment
    // keeps payment_status 'pending' forever (no terminal failure status
    // exists), so without this the screen would show "processing payment"
    // indefinitely. Guarded so a repeated poll can't stack modals.
    if (paymentState === ORDER_PAYMENT_STATES.FAILED && !paymentFailureHandledRef.current) {
      // While the return handler owns the outcome this is not final for the screen: it keeps
      // polling, so a handler that ends without an answer (its ask errored) still leaves the
      // failure to be reported here once its budget is spent, not swallowed as handled.
      if (Date.now() < returnHandlerOwnsFailureUntil.current) {
        return ORDER_PAYMENT_STATES.AWAITING_PAYMENT;
      }

      paymentFailureHandledRef.current = true;
      orderFailedHandler();

      return paymentState;
    }

    notePaymentStatus(status);
    setPaymentStatus(status);

    return paymentState ?? null;
  }

  async function loadOrderInfo() {
    const successHandler = async (data: Order) => {
      if (isAfterPurchase && !orderRef.current) {
        if (
          hasCashbackProgram &&
          (data.cashback_account_operations?.length !== 0 || data.coupon_type === COUPON_TYPES.CASHBACK)
        ) {
          ProfileManager.getCashbackAccount(company?.id);
        }
      }

      setOrder(data);
      addOngoingOrder(data);
      isStripeCheckout && refreshSavedPaymentOptions(data);

      if (!orderRef.current && isAfterPurchase) {
        setSearchParams({});

        trackCompletePurchase(data.payment, data);
        notify({
          message: t('success_messages.order_placed.title'),
          description: t('success_messages.order_placed.description'),
          position: 'top-center',
          duration: 10,
          hideProgressBar: false,
        });
      }
    };

    const errorHandler = () => alert.error({ description: t('error_messages.failed_to_load') });

    if (
      paymentStatusRef.current === null ||
      paymentStatusRef.current === ORDER_PAYMENT_STATUSES.PENDING ||
      loadingOrderRef.current
    ) {
      return;
    }

    setLoadingOrder(true);
    const { response, success } = await OrderManager.getOrder(uuid);
    setLoadingOrder(false);

    const data: Order | null = success ? (response?.data?.data ?? null) : null;

    if (data) {
      emptyOrderRetriesRef.current = 0;

      return successHandler(data);
    }

    // A success without the `{ data: Order }` envelope (Firefox resolves a cut-off XHR as one,
    // SHOP-WEB-46) is a failed load: retried with the same backoff as the first load, then reported.
    if (success && emptyOrderRetriesRef.current < ORDER_REFETCH_DELAYS_MS.length) {
      const delayMs = ORDER_REFETCH_DELAYS_MS[emptyOrderRetriesRef.current];
      emptyOrderRetriesRef.current += 1;
      clearTimeout(emptyOrderRetryTimerRef.current);
      emptyOrderRetryTimerRef.current = setTimeout(() => {
        if (!pollStoppedRef.current) {
          loadOrderInfo();
        }
      }, delayMs);

      return;
    }

    return errorHandler();
  }

  const onCashbackClaimed = () => {
    setOrder((current) => ({
      ...current,
      cashback_account_operations: collect(current.cashback_account_operations)
        .map((item) => ({ ...item, is_claimed: true }))
        .toArray(),
    }));
  };

  const config: IContent = {
    paymentStatus,
    order: orderRef.current,
    completeBasketItems,
    onCashbackClaimed,
    closeModal,
  };

  return (
    <Content.PageLoading {...config}>
      {/*
        The assertion target of every payment spec: reaching this screen with a real uuid is what
        "the order was placed" means end to end. uuid and payment status are exposed as data-*
        because the visible status is translated copy, and the uuid only otherwise appears in the
        URL — which a spec cannot use to tell a recovered draft from a newly created order.
      */}
      <div
        className={classnames('min-h-screen bg-gray-background', { 'pb-[200px]': isMobileApp })}
        data-test="order-details-screen"
        data-order-uuid={order?.uuid}
        data-payment-status={paymentStatus}
      >
        <div className="layout-container py-lg">
          <BackButton
            onClick={() => (isMobileApp ? closeModal?.() : navigate({ web: { to: SCREENS.HOME_SCREEN } }))}
            label={t('buttons.back_to_menu')}
          />
          <TestRestaurantNotice company={company} className="mt-md" />
          <div className="flex flex-col lg:flex-row gap-md lg:gap-lg mt-md">
            <div className="flex-1 min-w-0 space-y-md">
              <Content.LeftColumn {...config} />
            </div>
            <div className="w-full lg:w-[340px] shrink-0">
              <Content.OrderSummaryCard {...config} />
            </div>
          </div>
          {orderRef.current?.company && (
            <div className="lg:hidden mt-md">
              <Content.Help company={orderRef.current.company} />
            </div>
          )}
        </div>
      </div>
    </Content.PageLoading>
  );
};

const OrderDetailsScreen = (mobileProps: IOrderDetailsPage): ReactElement => {
  const params = useParams();
  const search = useUrlParams();

  const webProps = {
    ...params,
    ...search,
  };

  const props = mobileProps.uuid ? mobileProps : webProps;

  return (
    <Content.Validator {...props}>
      <Content {...props} />
    </Content.Validator>
  );
};

export default OrderDetailsScreen;

interface IContent {
  paymentStatus: ORDER_PAYMENT_STATUSES;
  order: Order;
  completeBasketItems: CompleteBasketItem[];
  onCashbackClaimed: () => void;
  closeModal: () => void;
}

const BackButton = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2xs text-sm text-brand-text-primary hover:text-brand-primary transition-colors"
  >
    <ChevronLeft className="w-4 h-4" />
    {label}
  </button>
);

Content.LeftColumn = (props: IContent) => {
  const { order, paymentStatus, closeModal } = props;
  const { isMobileApp } = useMobileApp();

  if (!(paymentStatus === ORDER_PAYMENT_STATUSES.PAID || paymentStatus === ORDER_PAYMENT_STATUSES.REFUNDED)) {
    return null;
  }

  if (!order) {
    return null;
  }

  return (
    <>
      <Content.StatusCard order={order} />
      {!isMobileApp && <OrderTracker order={order} className={sectionCard} />}
      <Content.Rewards {...props} />
      <Content.PickupDetails order={order} />
      <Content.CustomerDetails order={order} />
      <OrderRating
        order={order}
        classes={{ container: classnames(sectionCard, 'text-center'), wrapper: 'mx-auto' }}
        onSelected={closeModal}
      />
      <div className="hidden lg:block">
        <Content.Help company={order.company} />
      </div>
    </>
  );
};

Content.StatusCard = ({ order }: { order: Order }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { formatTime, formatDateTime } = useAppMethods();

  const status = getOrderStatus(order);

  const isDelivery = order?.method === ORDER_METHODS.DELIVERY;
  const isAccepted = status.key === 'confirmed';
  const isRejected = status.key === 'canceled';
  const isScheduled = !!order?.scheduled_for;

  const acceptedTimeLabel = useMemo(() => {
    if ((!isAccepted || !order?.fulfillment?.estimated_pickup_time) && !order?.scheduled_for) {
      return null;
    }

    const estimatedPickupTime = moment(order.fulfillment.estimated_pickup_time);

    return `${formatTime(estimatedPickupTime)} – ${formatTime(estimatedPickupTime.clone().add(10, 'minutes'))}`;
  }, [isAccepted, order?.fulfillment?.estimated_pickup_time, formatTime]);

  const dayLabel = useMemo(() => {
    const reference = order?.fulfillment?.estimated_pickup_time ?? order?.scheduled_for;

    if (!reference) {
      return null;
    }

    return moment().isSame(reference, 'day')
      ? t('labels.today')
      : formatDateTime(reference, { format: 'dddd, DD MMM' });
  }, [order?.fulfillment?.estimated_pickup_time, order?.scheduled_for, formatDateTime, t]);

  const isTimeChanged =
    !!order?.fulfillment?.initial_confirmation_time &&
    !!order?.fulfillment?.estimated_pickup_time &&
    order?.fulfillment?.initial_confirmation_time !== order?.fulfillment?.estimated_pickup_time;

  const initialTimeLabel = useMemo(() => {
    if (!isAccepted || !isTimeChanged) {
      return null;
    }

    const initial = moment(order.fulfillment.initial_confirmation_time);

    return `${formatTime(initial)} – ${formatTime(initial.clone().add(10, 'minutes'))}`;
  }, [isAccepted, isTimeChanged, order?.fulfillment?.initial_confirmation_time, formatTime]);

  const averageInterval = isDelivery
    ? order?.company?.average_order_delivery_time
    : order?.company?.average_order_preparation_time;

  const pendingEtaMinutes = useMemo(() => {
    if (isAccepted || isRejected || isScheduled || !averageInterval) {
      return null;
    }

    return HelperMethods.formatTimeInterval(averageInterval);
  }, [isAccepted, isRejected, isScheduled, averageInterval]);

  const fulfillmentAddress = isDelivery
    ? AddressHelpers.format({
        street_name: order?.delivery_street_name,
        street_number: order?.delivery_street_number,
        city: order?.delivery_city,
        format: order?.company?.region?.address_format,
      })
    : `${order?.company?.name ?? ''} · ${order?.company?.address ?? ''}`;

  const Icon = status.icon;
  const FulfillmentIcon = isDelivery ? Truck : ShoppingBag;

  return (
    <div className={sectionCard}>
      <div className="flex items-center gap-sm mb-sm">
        <Icon className={classnames('w-7 h-7 shrink-0', status.iconColor)} />
        <div className="min-w-0">
          <h5 className="text-md font-bold text-brand-text-primary">{t(`status.${status.key}`)}</h5>
          <p className="text-sm text-muted-foreground">
            {t('labels.order_number_short', { number: order?.fulfillment?.number ?? order?.id })}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-sm bg-muted/50 rounded-lg px-md py-sm">
        <FulfillmentIcon className="w-5 h-5 text-brand-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-brand-text-primary">
            {t(
              order?.method === ORDER_METHODS.ROOM_SERVICE
                ? `order_methods.${order.method}.${order.room_service_location?.type ?? RoomServiceLocationType.TableService}`
                : `order_methods.${order?.method}`,
              { defaultValue: '' },
            )}
          </p>
          <p className="text-xs text-muted-foreground truncate">{fulfillmentAddress}</p>
        </div>
        {pendingEtaMinutes && (
          <div className="text-right shrink-0">
            <p className="text-lg font-bold text-brand-text-primary leading-tight tabular-nums">{pendingEtaMinutes}</p>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{t('eta.min_label')}</p>
          </div>
        )}
        {!pendingEtaMinutes && acceptedTimeLabel && (
          <div className="text-right shrink-0">
            {dayLabel && <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{dayLabel}</p>}
            <p
              className={classnames('font-bold text-brand-text-primary tabular-nums leading-tight', {
                'text-base': isTimeChanged,
                'text-lg': !isTimeChanged,
              })}
            >
              {acceptedTimeLabel}
            </p>
            {isTimeChanged && initialTimeLabel && (
              <p className="text-[10px] text-muted-foreground line-through tabular-nums mt-[2px]">
                {t('labels.was_prefix')} {initialTimeLabel}
              </p>
            )}
          </div>
        )}
      </div>
      <PickupTimeChangedNotice order={order} className="mt-sm" />
    </div>
  );
};

Content.Rewards = ({ order, onCashbackClaimed }: IContent) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { isLoggedIn } = useAuth();
  const { toggleLoginModal } = useAppMethods();
  const {
    hasCashbackProgram,
    cashbackProgram,
    balance: rewardBalance,
    formatPointsAsMoney,
  } = useCashbackProgram({ company: order.company });

  const earnedPoints = useMemo(
    () => +collect(order?.cashback_account_operations).sum('value'),
    [order?.cashback_account_operations],
  );

  const hasUnclaimedGuestPoints = useMemo(
    () => +collect(order?.cashback_account_operations).where('is_claimed', false).sum('value') > 0,
    [order?.cashback_account_operations],
  );

  if (!hasCashbackProgram || earnedPoints <= 0) {
    return null;
  }

  const programName = cashbackProgram?.title ?? `${order?.company?.name ?? ''} Rewards`.trim();

  if (isLoggedIn) {
    const newBalance = rewardBalance;
    const maxRedeemable = cashbackProgram?.max_spending_limit ?? 0;
    const minRedeemable = cashbackProgram?.min_spending_limit ?? 0;
    const maxRewardValue = maxRedeemable > 0 ? formatPointsAsMoney(maxRedeemable) : null;
    const rewardValue = formatPointsAsMoney(newBalance);
    const progress = minRedeemable > 0 ? Math.min((newBalance / minRedeemable) * 100, 100) : 100;
    const hasReachedMinThreshold = minRedeemable > 0 && newBalance >= minRedeemable;
    const pointsToReachMin = Math.max(minRedeemable - newBalance, 0);

    return (
      <div className={rewardCard}>
        <div className="flex justify-center">
          <div className={rewardPill}>
            <Star className="w-3 h-3 fill-brand-primary text-brand-primary" />
            {programName}
          </div>
        </div>
        <div className="text-center space-y-2xs mt-sm">
          <p className="text-[30px] md:text-[36px] font-bold text-brand-primary leading-none tabular-nums">
            +{earnedPoints}
          </p>
          <p className="text-sm text-muted-foreground">{t('rewards.member.from_order')}</p>
        </div>
        <div className="flex items-stretch justify-center gap-sm mt-md">
          <div className={rewardStat}>
            <p className="text-[22px] font-bold text-brand-primary tabular-nums">{newBalance}</p>
            <p className="text-[10px] text-muted-foreground mt-[2px]">{t('rewards.member.total_balance')}</p>
          </div>
          <div className={rewardStat}>
            <p className="text-[22px] font-bold text-brand-primary">{rewardValue}</p>
            <p className="text-[10px] text-muted-foreground mt-[2px]">{t('rewards.member.reward_value')}</p>
          </div>
        </div>
        {newBalance > 0 && (
          <div className="mt-md mx-auto w-full max-w-[400px]">
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden mb-2xs">
              <div
                className="h-full bg-brand-primary rounded-full transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p
              className={classnames('text-[11px] font-medium text-center mt-sm', {
                'text-success': hasReachedMinThreshold,
                'text-muted-foreground': !hasReachedMinThreshold,
              })}
            >
              {hasReachedMinThreshold
                ? t('rewards.member.redeem_hint', { points: maxRedeemable, amount: maxRewardValue })
                : t('rewards.member.points_to_redeem', { points: pointsToReachMin })}
            </p>
          </div>
        )}
      </div>
    );
  }

  if (!hasUnclaimedGuestPoints) {
    return null;
  }

  const guestRewardValue = formatPointsAsMoney(earnedPoints);

  const onClaim = () => {
    toggleLoginModal({
      openModal: true,
      isStatic: true,
      initialPhoneNumber: order.customer_phone_number,
      onCloseRedirect: (success) => success && onCashbackClaimed?.(),
    });
  };

  return (
    <div className={rewardCard}>
      <div className="text-center">
        <div className="flex justify-center">
          <div className={rewardPill}>
            <Star className="w-3 h-3 fill-brand-primary text-brand-primary" />
            {programName}
          </div>
        </div>
        <h2 className="text-[20px] md:text-[24px] font-bold text-brand-text-primary leading-tight mt-sm">
          <Trans
            t={t}
            values={{ points: earnedPoints }}
            components={{ highlight: <span className="text-brand-primary" /> }}
          >
            rewards.guest.earned_heading
          </Trans>
        </h2>
        <p className="text-sm text-muted-foreground mt-2xs">
          <Trans
            t={t}
            values={{ amount: guestRewardValue }}
            components={{ strong: <strong className="text-brand-text-primary" /> }}
          >
            rewards.guest.worth_on_next
          </Trans>
        </p>
        <div className="flex items-stretch justify-center gap-sm mt-md">
          <div className={rewardStat}>
            <p className="text-[22px] font-bold text-brand-primary tabular-nums">{earnedPoints} pts</p>
            <p className="text-[10px] text-muted-foreground mt-[2px]">{t('rewards.guest.ready_to_claim')}</p>
          </div>
          <div className={rewardStat}>
            <p className="text-[22px] font-bold text-brand-primary">{guestRewardValue}</p>
            <p className="text-[10px] text-muted-foreground mt-[2px]">{t('rewards.guest.next_order_discount')}</p>
          </div>
        </div>
        <button type="button" onClick={onClaim} className={claimButton}>
          <Gift className="w-4 h-4" />
          {t('rewards.guest.claim_cta', { points: earnedPoints })}
        </button>
        <p className="text-[11px] text-muted-foreground mt-xs">{t('rewards.guest.claim_note')}</p>
      </div>
    </div>
  );
};

Content.PickupDetails = ({ order }: { order: Order }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const isDelivery = order?.method === ORDER_METHODS.DELIVERY;
  const title = isDelivery ? t('labels.delivery_details') : t('labels.pickup_details');

  return (
    <div className={sectionCard}>
      <div className="flex items-center gap-2xs mb-sm">
        <MapPin className="w-[18px] h-[18px] text-brand-primary shrink-0" />
        <p className="text-sm font-bold text-brand-text-primary">{title}</p>
      </div>
      <CompanyDescription company={order.company} shouldOpenMap />
      <DeliveryTracker order={order} className="mt-md w-full" />
      {!isDelivery && (
        <div className="bg-muted/50 rounded-lg px-sm py-xs text-sm text-muted-foreground mt-md">
          <Trans
            t={t}
            values={{ number: order?.fulfillment?.number ?? order?.id }}
            components={{ strong: <strong className="text-brand-text-primary" /> }}
          >
            labels.pickup_instruction
          </Trans>
        </div>
      )}
    </div>
  );
};

Content.CustomerDetails = ({ order }: { order: Order }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { company } = order;

  return (
    <div className={sectionCard}>
      <p className="text-sm font-bold text-brand-text-primary mb-sm">{t('labels.your_details')}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
        <div className="space-y-2xs">
          <p className="text-xs text-muted-foreground">{t('labels.contact')}</p>
          <p className="text-sm text-brand-text-primary">
            {order.customer_first_name} {order.customer_last_name}
          </p>
          {order.customer_phone_number && (
            <p className="text-sm text-muted-foreground flex items-center gap-2xs">
              <Phone className="w-3 h-3" /> {order.customer_phone_number}
            </p>
          )}
          {order.customer_email && (
            <p className="text-sm text-muted-foreground flex items-center gap-2xs">
              <Mail className="w-3 h-3" /> {order.customer_email}
            </p>
          )}
        </div>
        {order.total > 0 && order.payment?.payment_method_id && (
          <div className="space-y-2xs">
            <p className="text-xs text-muted-foreground">{t('labels.payment')}</p>
            <div className="text-sm text-brand-text-primary flex items-center gap-2xs">
              <PaymentDetails
                payment_method_id={order.payment.payment_method_id}
                payment_method_brand={order.payment.payment_method_brand}
                payment_method_details={order.payment.payment_method_details}
                stripe_payment_method_type={order.payment.stripe_payment_method_type}
                amount={order.total}
                classes={{ text: 'text-sm text-muted-foreground' }}
              />
            </div>
          </div>
        )}
        {order.method === ORDER_METHODS.DELIVERY && (
          <div className="space-y-2xs">
            <p className="text-xs text-muted-foreground">{t('labels.delivery')}</p>
            <p className="text-sm text-brand-text-primary">
              {order.customer_first_name} {order.customer_last_name}
            </p>
            <p className="text-sm text-muted-foreground">
              {AddressHelpers.format({
                street_name: order.delivery_street_name,
                street_number: order.delivery_street_number,
                city: order.delivery_city,
                state: order.delivery_state,
                zip_code: order.delivery_zip_code,
                format: company?.region?.address_format,
              })}
            </p>
          </div>
        )}
        {order.is_receipt_required && order.payment?.payment_method_id !== PaymentMethod.Cash && (
          <div className="space-y-2xs">
            <p className="text-xs text-muted-foreground">{t('labels.billing_info')}</p>
            {Boolean(order.invoice_company_name) && (
              <p className="text-sm text-brand-text-primary">{order.invoice_company_name}</p>
            )}
            {Boolean(order.invoice_tax_id) && <p className="text-sm text-muted-foreground">{order.invoice_tax_id}</p>}
            {Boolean(order.invoice_first_name) && Boolean(order.invoice_last_name) && (
              <p className="text-sm text-brand-text-primary">
                {order.invoice_first_name} {order.invoice_last_name}
              </p>
            )}
            <p className="text-sm text-muted-foreground">
              {AddressHelpers.format({
                street_name: order.invoice_address,
                city: order.invoice_city,
                format: company?.region?.address_format,
                zip_code: order.invoice_zip_code,
              })}
            </p>
          </div>
        )}
        {Boolean(order.customer_note) && (
          <div className="space-y-2xs sm:col-span-2">
            <p className="text-xs text-muted-foreground">{t('labels.order_note')}</p>
            <p className="mp-mask text-sm text-brand-text-primary break-words">{order.customer_note}</p>
          </div>
        )}
      </div>
    </div>
  );
};

Content.Help = ({ company }: { company: CompanyDetailsInterface }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });

  if (!company?.phone_number) {
    return null;
  }

  return (
    <div className="flex items-center justify-center gap-2xs text-xs text-muted-foreground py-xs">
      <HelpCircle className="w-3.5 h-3.5" />
      <span>
        {t('text.need_help')}{' '}
        <a
          href={`tel:${company.phone_number}`}
          className="text-brand-text-primary font-medium hover:text-brand-primary transition-colors"
        >
          {company.phone_number}
        </a>
      </span>
    </div>
  );
};

Content.OrderSummaryCard = ({ order, completeBasketItems }: IContent) => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { formatCurrency } = useAppMethods();

  if (!order) {
    return null;
  }

  return (
    <div className={classnames(sectionCard, 'lg:sticky lg:top-6 space-y-md')}>
      <h3 className="text-base font-bold text-brand-text-primary">{t('labels.order_summary')}</h3>
      <div className="space-y-sm">
        {completeBasketItems?.map((item: CompleteBasketItem, index: number) => (
          <div key={`line-item-${index}`} className="space-y-2xs">
            <div className="flex items-start justify-between gap-sm text-sm">
              <span className="text-brand-text-primary">
                {item.quantity}x {item.entry?.name}
              </span>
              <span className="text-muted-foreground tabular-nums shrink-0">{formatCurrency(item.actual_price)}</span>
            </div>
            <BasketItemModifiers item={item} />
            {item.note && <p className="text-xs text-muted-foreground break-words">{item.note}</p>}
          </div>
        ))}
      </div>
      <div className="border-t-xs border-brand-border pt-md">
        <OrderPaymentBreakdown order={order} />
      </div>
    </div>
  );
};

Content.PageLoading = ({ order, paymentStatus, children }: IContent & PropsWithChildren): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });
  const { isMobileApp } = useMobileApp();

  if (!order) {
    return (
      <LoadingPage
        description={
          paymentStatus === ORDER_PAYMENT_STATUSES.PENDING && !isMobileApp ? t('loading.processing_payment') : undefined
        }
      />
    );
  }

  return <>{children}</>;
};

Content.Validator = ({ uuid, closeModal, children }: IOrderDetailsPage & PropsWithChildren): ReactElement => {
  // A backgrounded tab (e.g. while the customer is in their banking app) can have every request fail
  // with no status; refetching on return lets the page recover instead of staying on "contact support".
  const {
    data,
    isLoading,
    isFetching,
    error: apiError,
    requestId,
    refetch,
  } = useGetOrderQuery(uuid, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const error = apiError as ErrorResponse & { data?: { code?: string } };
  const isNotFound = error?.status === 404;
  const hasOrder = !!data?.data;
  const refetchesRef = useRef(0);
  const manualRetriesRef = useRef(0);
  const startedAtRef = useRef(Date.now());
  const isWaitingForRefetch =
    !isLoading && !isFetching && !isNotFound && !hasOrder && refetchesRef.current < ORDER_REFETCH_DELAYS_MS.length;

  useEffect(() => {
    if (isNotFound) {
      WindowResource.replace('/404');
    }
  }, [isNotFound]);

  // Covers a request that ended without a response as well as a 2xx whose body is not the
  // `{ data: Order }` envelope (FOODAMIGOS-SHOP-WEB-2DA). A hidden page waits until it is visible
  // again, and coming back online or to the foreground retries at once instead of waiting out the delay.
  useEffect(() => {
    if (!isWaitingForRefetch) {
      return;
    }

    const refetchNow = () => {
      if (document.visibilityState === 'hidden') {
        return;
      }

      stopWaiting();
      refetchesRef.current += 1;
      refetch();
    };

    const timer = setTimeout(refetchNow, ORDER_REFETCH_DELAYS_MS[refetchesRef.current]);
    window.addEventListener('online', refetchNow);
    document.addEventListener('visibilitychange', refetchNow);

    function stopWaiting() {
      clearTimeout(timer);
      window.removeEventListener('online', refetchNow);
      document.removeEventListener('visibilitychange', refetchNow);
    }

    return stopWaiting;
  }, [isWaitingForRefetch, requestId]);

  if (isNotFound) return null;

  if (hasOrder) return children as ReactElement;

  if (isLoading || isFetching || isWaitingForRefetch) return <LoadingPage />;

  const retry = () => {
    manualRetriesRef.current += 1;
    refetchesRef.current = 0;
    refetch();
  };

  return (
    <Content.LoadFailed
      closeModal={closeModal}
      onRetry={retry}
      // A manual retry that fails again is the failure already reported, not a new one.
      diagnostics={
        manualRetriesRef.current > 0
          ? null
          : {
              order_uuid: uuid,
              refetches: refetchesRef.current,
              error_status: error?.status ?? null,
              error_code: error?.data?.code ?? null,
              response_type: data === null ? 'null' : typeof data,
              elapsed_ms: Date.now() - startedAtRef.current,
            }
      }
    />
  );
};

Content.LoadFailed = ({
  closeModal,
  diagnostics,
  onRetry,
}: Pick<IOrderDetailsPage, 'closeModal'> & {
  diagnostics: DiagnosticAttrs | null;
  onRetry: () => void;
}): ReactElement => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:OrderDetailsScreen' });

  useEffect(() => (diagnostics ? pageDiagnostics.orderLoadFailed(diagnostics) : undefined), []);

  const { isMobileApp } = useMobileApp();
  const { navigate } = useRouter();
  const company = useSelector((state: StoreInterface) => state.initialData?.company);
  const supportEmail = company?.service_provider?.support_email ?? 'support@foodamigos.io';

  return (
    <main
      className="fixed inset-0 flex flex-col h-full w-full justify-center items-center lg:w-1/2 mx-auto px-md"
      data-test="order-details-load-failed"
    >
      <IllustrationExplainer
        illustration={<ErrorIllustration className="h-20 w-20" />}
        title={t('load_failed.title')}
        description={t('load_failed.description')}
      />
      <div className="flex flex-col items-center gap-2xs mt-md text-sm">
        <a href={`mailto:${supportEmail}`} className="font-medium text-brand-text-primary hover:text-brand-primary">
          {supportEmail}
        </a>
        {company?.phone_number && (
          <a
            href={`tel:${company.phone_number}`}
            className="font-medium text-brand-text-primary hover:text-brand-primary"
          >
            {company.phone_number}
          </a>
        )}
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="lg:mt-xl mt-lg inline-flex items-center justify-center rounded-[9999px] bg-black text-white text-sm font-semibold px-xl py-sm hover:opacity-90 transition-opacity"
        data-test="order-details-load-retry"
      >
        {t('buttons.try_again')}
      </button>
      <button
        type="button"
        onClick={() => (isMobileApp ? closeModal?.() : navigate({ web: { to: SCREENS.HOME_SCREEN } }))}
        className="mt-sm text-sm font-medium text-brand-text-primary hover:text-brand-primary"
      >
        {t('buttons.back_to_menu')}
      </button>
    </main>
  );
};

function getOrderStatus(order: Order): {
  key: 'canceled' | 'received' | 'confirmed';
  icon: typeof CheckCircle;
  iconColor: string;
} {
  const fulfillmentStatus = order?.fulfillment?.status;
  const isRejected = fulfillmentStatus === ORDER_FULFILLMENT_STATUSES.REJECTED;
  const isAccepted = order?.fulfillment?.events?.some((event) => event.status === ORDER_FULFILLMENT_STATUSES.ACCEPTED);

  if (isRejected) {
    return { key: 'canceled', icon: XCircle, iconColor: 'text-danger' };
  }

  if (isAccepted) {
    return { key: 'confirmed', icon: CheckCircle, iconColor: 'text-success' };
  }

  return { key: 'received', icon: CheckCircle, iconColor: 'text-success' };
}

const sectionCard = 'rounded-xl border-xs border-brand-border bg-brand-card p-md';

const rewardCard =
  'rounded-xl border-xs border-brand-primary/20 bg-gradient-to-br from-brand-primary/5 via-brand-card to-brand-primary/5 p-md';

const rewardPill =
  'inline-flex items-center gap-2xs bg-brand-primary/15 border-xs border-brand-primary/25 rounded-full px-sm py-2xs text-[11px] font-semibold text-brand-primary uppercase tracking-wider';

const rewardStat =
  'bg-brand-card border-xs border-brand-border rounded-xl px-md py-sm text-center flex-1 max-w-[160px]';

const claimButton =
  'mt-md bg-brand-primary text-brand-button-primary-text font-bold px-lg py-sm rounded-full text-sm hover:opacity-90 transition-all inline-flex items-center gap-xs';
